import { useState, useEffect, useRef, useMemo } from "react";
import styles from "./styles.module.css";
import DailiesWorkflow from "../dailies/dailiesPatientDetails";
import ReadingReviewSession from "./review-session/ReadingReviewSession";
import {
  clearOpenPatient,
  getOpenPatient,
  heldTimerFor,
} from "./review-session/openPatient";
import OORReport, {
  GenerateReportButton,
} from "../../../components/case-manager/oor-report";
import { ReportLifecycleProvider } from "../../../components/case-manager/oor-report/ReportLifecycleContext";
import PriorityScore from "./priority";
import NurseTasks from "../../dashboards/nurse";
import { usePatients } from "../../../hooks/useMyTasksQueries";
import { canAccessNurseTasks, getUserFromStorage } from "../../../utils/authorization";
import { canAccessFeature } from "../../../utils/authorization";
import DailiesOORTracker from "../../../components/shared-modules/time-tracker/DailiesOORTracker";
import {
  Tabs,
  TabList,
  TabTrigger,
  TabPanels,
  Panel,
} from "../../../components/ui/tab";

// SCRUM-844: Readings Review 2.0 (session-based) is built alongside the
// classic Readings Review so either can be shown ("Reading Review: How It
// Works" - "we can switch back at any time"). false shows the classic one.
const USE_SESSION_READING_REVIEW = true;

const MyTasks = () => {
  const user = getUserFromStorage();

  // Re-read patientId from sessionStorage reactively so tracker updates
  // when the user navigates between patients inside DailiesWorkflow.
  const [currentPatientId, setCurrentPatientId] = useState(() => {
    if (!USE_SESSION_READING_REVIEW) return sessionStorage.getItem("_pt_id");
    // A marker left by a reload mid-review would start the timer on the
    // selection screen; the workspace sets it again when a patient opens.
    clearOpenPatient();
    return null;
  });

  // Track the selected patient's OWN location alongside the patient id so the
  // monitoring time tracker stores time under the patient's location, not the
  // viewer's current location (SCRUM-157). DailiesPatientDetails writes
  // "_pt_loc_id" when each patient's details resolve.
  const [currentPatientLocId, setCurrentPatientLocId] = useState(
    () => sessionStorage.getItem("_pt_loc_id")
  );

  // SCRUM-850: the review session's paused count for the open patient.
  const heldTimer = useMemo(
    () => (USE_SESSION_READING_REVIEW && currentPatientId ? heldTimerFor(currentPatientId) : undefined),
    [currentPatientId]
  );

  const [timerValues, setTimerValues] = useState({ elapsedTimeRPM: 0, elapsedTimeCCM: 0 });

  const [triggerSave, setTriggerSave] = useState(false);

  // Listen for trigger-save events dispatched by DailiesWorkflow
  useEffect(() => {
    const handleTriggerSave = () => {
      setTriggerSave(true);
      setTimeout(() => setTriggerSave(false), 100);
    };
    window.addEventListener("trigger-save", handleTriggerSave);
    return () => window.removeEventListener("trigger-save", handleTriggerSave);
  }, []);

  // Poll sessionStorage so the tracker key updates when the patient changes.
  // DailiesPatientDetails writes "_pt_id" on every patient navigation. The
  // session-based Readings Review instead sets its open patient only while
  // the reviewer is on a patient, so the timer doesn't run (on a leftover
  // _pt_id) on its selection screens.
  useEffect(() => {
    const interval = setInterval(() => {
      const id = USE_SESSION_READING_REVIEW ? getOpenPatient() : sessionStorage.getItem("_pt_id");
      setCurrentPatientId((prev) => (prev !== id ? id : prev));
      const locId = sessionStorage.getItem("_pt_loc_id");
      setCurrentPatientLocId((prev) => (prev !== locId ? locId : prev));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const showNurseTab   = canAccessNurseTasks(user);
  const showPriorityList = canAccessFeature(user, "my_tasks.priority_list");
  const showDailies    = canAccessFeature(user, "my_tasks.dailies");
  const showOutOfRange = canAccessFeature(user, "my_tasks.out_of_range");

  const [activeTab, setActiveTab] = useState(() => {
    const stored = sessionStorage.getItem("my-tasks/active-tab");
    if (stored) return stored;
    if (showPriorityList) return "priority";
    if (showDailies)      return "dailies";
    if (showOutOfRange)   return "outOfRange";
    if (showNurseTab)     return "nurseTasks";
    return "dailies";
  });

  const { data: outOfRangePatients = [] } = usePatients({
    outOfRange: true,
    enabled: showOutOfRange && activeTab === "outOfRange",
  });

  useEffect(() => {
    if (showOutOfRange && activeTab === "outOfRange" && outOfRangePatients.length > 0) {
      sessionStorage.setItem("_ap", JSON.stringify(outOfRangePatients.map((p) => p.id)));
    }
  }, [activeTab, outOfRangePatients, showOutOfRange]);

  useEffect(() => {
    if (showOutOfRange && activeTab === "outOfRange") {
      sessionStorage.setItem("caseManagerFilterState", "outOfRange");
    }
  }, [activeTab, showOutOfRange]);

  useEffect(() => {
    if (activeTab) sessionStorage.setItem("my-tasks/active-tab", activeTab);
  }, [activeTab]);

  // Listen for custom event to switch tabs (e.g., from completion modal)
  useEffect(() => {
    const handleSwitchTab = (event) => {
      const { tab } = event.detail;
      if (tab) setActiveTab(tab);
    };

    window.addEventListener("switch-my-tasks-tab", handleSwitchTab);
    return () => window.removeEventListener("switch-my-tasks-tab", handleSwitchTab);
  }, []);

  return (
    <ReportLifecycleProvider>
    <div className={styles.switchBar}>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabList className={styles.tabsHeader}>

          {/* Left: tab trigger buttons */}
          <div className={styles.tabsLeft}>
            {showPriorityList && (
              <TabTrigger value="priority">Priority List</TabTrigger>
            )}
            {showDailies && (
              /* Figma label: "Readings Review" (no trailing s) */
              <TabTrigger value="dailies">Readings Review</TabTrigger>
            )}
            {showOutOfRange && (
              <TabTrigger value="outOfRange">Out of Range</TabTrigger>
            )}
            {showNurseTab && (
              <TabTrigger value="nurseTasks">Nurse Tasks</TabTrigger>
            )}
          </div>

          {/* Center: timer — only visible while Dailies tab is active
              Figma layout: pause btn | gray pill "HH:MM:SS RPM" | clock btn
              key={currentPatientId} remounts tracker when patient changes   */}
          <div className={styles.tabsCenter}>
            {activeTab === "dailies" && currentPatientId && (
              <DailiesOORTracker
                key={currentPatientId}
                triggerSaveSignal={triggerSave}
                patientId={currentPatientId}
                locationId={currentPatientLocId}
                flow="Dailies"
                onTick={(values) => setTimerValues(values)}
                // SCRUM-850: continuing a paused review session (in this
                // or another browser tab) picks the count back up.
                heldTimer={heldTimer}
                noteSavesTime={USE_SESSION_READING_REVIEW}
              />
            )}
          </div>

          {/* Right: OOR Report actions — Figma puts Generate report level
              with the tab triggers (SCRUM-191 stub) */}
          {showOutOfRange && activeTab === "outOfRange" && (
            <div className={styles.tabsRight}>
              <GenerateReportButton />
            </div>
          )}

        </TabList>

        <div className={styles.contentWrapper}>
          <TabPanels>
            {showNurseTab && (
              <Panel value="nurseTasks">
                <NurseTasks />
              </Panel>
            )}
            {showPriorityList && (
              <Panel value="priority">
                <PriorityScore />
              </Panel>
            )}
            {showDailies && (
              <Panel value="dailies">
                {USE_SESSION_READING_REVIEW ? (
                  <ReadingReviewSession timerValues={timerValues} />
                ) : (
                  <DailiesWorkflow timerValues={timerValues} />
                )}
              </Panel>
            )}
            {showOutOfRange && (
              <Panel value="outOfRange">
                {/* OOR Report module (SCRUM-134+): replaces the old
                    out-of-range patient list per the new Figma designs. */}
                <OORReport />
              </Panel>
            )}
          </TabPanels>
        </div>
      </Tabs>
    </div>
    </ReportLifecycleProvider>
  );
};

export default MyTasks;